import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Trophy } from 'lucide-react';

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error('Please fill in all fields');
            return;
        }

        setLoading(true);
        try {
            const user = await login(email, password);
            toast.success('Welcome back!');
            if (!user.role) {
                navigate('/onboarding');
            } else {
                navigate('/');
            }
        } catch (error) {
            console.error('Login error:', error);
            toast.error(error.response?.data?.detail || 'Invalid credentials');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-black flex flex-col" data-testid="login-page">
            {/* Header */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-8"
                >
                    <div className="w-20 h-20 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4 glow-green">
                        <Trophy className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="font-heading text-4xl font-bold text-white uppercase tracking-tight">
                        OneGame
                    </h1>
                    <p className="text-white/60 mt-2">The home of football</p>
                </motion.div>

                <motion.form
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    onSubmit={handleSubmit}
                    className="w-full max-w-sm space-y-4"
                >
                    <div className="space-y-2">
                        <Label htmlFor="email" className="text-white/80">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="your@email.com"
                            className="bg-surface border-border text-white placeholder:text-white/40 h-12"
                            data-testid="login-email-input"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="password" className="text-white/80">Password</Label>
                        <div className="relative">
                            <Input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="bg-surface border-border text-white placeholder:text-white/40 h-12 pr-12"
                                data-testid="login-password-input"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                            >
                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>

                    <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-primary hover:bg-primary-hover text-white font-semibold h-12 rounded-full mt-6"
                        data-testid="login-submit-button"
                    >
                        {loading ? 'Signing in...' : 'Sign In'}
                    </Button>
                </motion.form>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="mt-6 text-white/60"
                >
                    Don't have an account?{' '}
                    <Link to="/signup" className="text-primary font-medium hover:underline" data-testid="signup-link">
                        Sign Up
                    </Link>
                </motion.p>

                {/* Demo Credentials */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="mt-8 p-4 bg-surface rounded-xl border border-border"
                >
                    <p className="text-white/60 text-sm text-center mb-2">Demo Credentials</p>
                    <p className="text-white/80 text-sm text-center">
                        Email: <span className="text-primary">demo@onegame.com</span>
                    </p>
                    <p className="text-white/80 text-sm text-center">
                        Password: <span className="text-primary">demo123</span>
                    </p>
                </motion.div>
            </div>
        </div>
    );
};

export default LoginPage;
